import type { ComponentProps } from 'react'

import { useTranslation } from 'react-i18next'

import { Box } from '@enechain/ecloud-designsystem'
import { FaBellRegular } from '@enechain/ecloud-icons'

import { useAnnouncementReadState } from '~/hooks/announcement/useAnnouncementReadState'
import { useListAnnouncements } from '~/hooks/announcement/useListAnnouncements'
import {
  hasUnreadAnnouncement,
  latestPublishedAt,
} from '~/models/announcementUnread'

// 告知はリリース時にしか増えないため定期取得はせず、起動時と window focus で
// 取り直す。focus のたびに RPC を打たないよう 5 分は cache を使う。
const STALE_TIME_MS = 5 * 60 * 1000

const useHasUnreadAnnouncement = (): boolean => {
  // 取得失敗の間は「未読なし」(`shared-components.md` AC-SC-034/035 の「取得に失敗
  // している間はドットを表示しない」)。data の有無だけで判定しないのは、再取得の失敗では
  // react-query が直前の data を残すため。
  const { data, isError } = useListAnnouncements({
    throwOnError: false,
    refetchOnWindowFocus: true,
    staleTime: STALE_TIME_MS,
  })
  const { lastReadPublishedAt } = useAnnouncementReadState()
  return hasUnreadAnnouncement(
    latestPublishedAt(isError ? [] : (data?.announcements ?? [])),
    lastReadPublishedAt,
  )
}

/**
 * サイドメニュー「お知らせ」の icon。未読がある間は bell の右上にドットを重ねる。
 *
 * DS の menu item には badge 用の slot が無く、`IconLeft` の差し替えなら展開・
 * 折りたたみの両方で icon と同じ位置に出せる (item を包んで絶対配置すると DS の
 * 内部 layout に位置が依存する)。design `.nav-alert-dot` の pulse animation は
 * メニューの他要素より目立ちすぎるため付けない (Design Note D-5、Des 確認待ち)。
 */
export const AnnouncementsMenuIcon = ({
  'aria-hidden': ariaHidden,
  ...iconProps
}: ComponentProps<typeof FaBellRegular>): React.ReactElement => {
  const { t } = useTranslation()
  const hasUnread = useHasUnreadAnnouncement()

  if (!hasUnread) {
    return <FaBellRegular aria-hidden={ariaHidden} {...iconProps} />
  }

  // DS は IconLeft に aria-hidden を渡すが、未読は装飾ではなく状態なので
  // 読み上げ対象にする (ドットに role="status" を付けると出現のたびに読み上げられる)。
  return (
    <Box
      as="span"
      position="relative"
      display="inline-flex"
      aria-label={t('common:nav.announcementsUnread')}
      role="img"
    >
      <FaBellRegular aria-hidden {...iconProps} />
      <Box
        as="span"
        position="absolute"
        top="0"
        right="0"
        boxSize="8px"
        borderRadius="full"
        pointerEvents="none"
        aria-hidden
        backgroundColor="Object.negativeMid"
      />
    </Box>
  )
}
